// components/admin/CategoryTranslations.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Translate as TranslateIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  CloudSync as SyncIcon
} from '@mui/icons-material';
import { useNotifications } from '../../context/NotificationContext';
import { useCategories } from '../../context/CategoryContext';
import categoryTranslationService from '../../services/categoryTranslationService';

const LANGUAGES = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' }
];

/**
 * Page d'administration des traductions des catégories
 */
const CategoryTranslations = () => {
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [bulkTranslating, setBulkTranslating] = useState(false);
  const [categories, setCategories] = useState([]);
  const [editDialog, setEditDialog] = useState({ open: false, category: null, language: null });
  const [translationValue, setTranslationValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const { showNotification } = useNotifications();
  const { categoriesList } = useCategories();

  /**
   * Charger les catégories avec leurs traductions
   */
  const loadCategories = async () => {
    setLoading(true);
    try {
      // Utiliser l'API spécialisée pour récupérer les catégories avec leurs traductions
      const data = await categoryTranslationService.getCategoryTranslations();
      console.log('📊 Données reçues du service:', data);
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
      showNotification({
        type: 'error',
        title: 'Erreur',
        message: 'Impossible de charger les catégories avec leurs traductions'
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Régénérer les fichiers de traduction
   */
  const handleRegenerateTranslations = async () => {
    setRegenerating(true);
    try {
      const result = await categoryTranslationService.regenerateTranslations();
      
      if (result.success) {
        showNotification({
          type: 'success',
          title: 'Succès',
          message: 'Fichiers de traduction régénérés avec succès'
        });
      } else {
        throw new Error(result.message || 'Erreur lors de la régénération');
      }
    } catch (error) {
      console.error('Erreur lors de la régénération:', error);
      showNotification({
        type: 'error',
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors de la régénération des traductions'
      });
    } finally {
      setRegenerating(false);
    }
  };

  /**
   * Traduire en masse toutes les catégories (titre + description) via DeepL
   */
  const handleBulkTranslateCategories = async () => {
    setBulkTranslating(true);
    try {
      const result = await categoryTranslationService.autoTranslateAllCategories();

      if (result.success) {
        const stats = result.stats;
        const errorCount = Number(stats?.errorCount || 0);
        const message = stats
          ? `Traduction terminée: ${stats.categoriesProcessed} catégories, ${stats.translationsUpserted} traductions.`
          : 'Traduction terminée.';

        showNotification({
          type: errorCount > 0 ? 'warning' : 'success',
          title: errorCount > 0 ? 'Traduction partielle' : 'Succès',
          message: errorCount > 0 ? `${message} (${errorCount} erreur(s) DeepL, fallback en français possible).` : message
        });

        await loadCategories();
      } else {
        throw new Error(result.message || 'Erreur lors de la traduction en masse');
      }
    } catch (error) {
      console.error('Erreur lors de la traduction en masse:', error);
      showNotification({
        type: 'error',
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors de la traduction en masse des catégories'
      });
    } finally {
      setBulkTranslating(false);
    }
  };

  /**
   * Ouvrir le dialogue d'édition
   */
  const handleEditTranslation = (category, language) => {
    const currentTranslation = category.translations?.find(t => t.language === language);
    setTranslationValue(currentTranslation?.name || category.name);
    setEditDialog({ open: true, category, language });
  };

  /**
   * Sauvegarder une traduction
   */
  const handleSaveTranslation = async () => {
    if (!editDialog.category || !editDialog.language || !translationValue.trim()) {
      return;
    }

    setSubmitting(true);
    try {
      await categoryTranslationService.updateCategoryTranslation(
        editDialog.category.id,
        editDialog.language,
        translationValue.trim()
      );

      showNotification({
        type: 'success',
        title: 'Succès',
        message: 'Traduction mise à jour avec succès'
      });

      // Recharger les catégories
      await loadCategories();
      setEditDialog({ open: false, category: null, language: null });
      setTranslationValue('');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showNotification({
        type: 'error',
        title: 'Erreur',
        message: 'Erreur lors de la sauvegarde de la traduction'
      });
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Obtenir la traduction pour une catégorie et une langue
   */
  const getTranslation = (category, languageCode) => {
    const translation = category.translations?.find(t => t.language === languageCode);
    return translation?.name || category.name;
  };

  /**
   * Vérifier si une traduction existe
   */
  const hasTranslation = (category, languageCode) => {
    return category.translations?.some(t => t.language === languageCode) || languageCode === 'fr';
  };

  useEffect(() => {
    loadCategories();
  }, []); // Charger une seule fois au montage du composant

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* En-tête */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          <TranslateIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Traductions des Catégories
        </Typography>
        <Typography variant="body1" color="textSecondary" paragraph>
          Gérez les traductions des catégories dans différentes langues et générez automatiquement les fichiers de traduction.
        </Typography>
      </Box>

      {/* Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Les fichiers de traduction sont automatiquement régénérés à chaque modification des catégories.
              </Alert>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadCategories}
                  disabled={loading}
                >
                  Actualiser
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<TranslateIcon />}
                  onClick={handleBulkTranslateCategories}
                  disabled={bulkTranslating || regenerating}
                  color="primary"
                >
                  {bulkTranslating ? 'Traduction...' : 'Traduire en Masse'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SyncIcon />}
                  onClick={handleRegenerateTranslations}
                  disabled={regenerating}
                  color="primary"
                >
                  {regenerating ? 'Génération...' : 'Régénérer les Fichiers'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {LANGUAGES.map((lang) => (
          <Grid item xs={12} md={4} key={lang.code}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h2" component="div" sx={{ mb: 1 }}>
                  {lang.flag}
                </Typography>
                <Typography variant="h6" component="div">
                  {lang.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {categories.filter(cat => hasTranslation(cat, lang.code)).length} / {categories.length} catégories
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={`${Math.round((categories.filter(cat => hasTranslation(cat, lang.code)).length / categories.length) * 100)}% complété`}
                    color={categories.filter(cat => hasTranslation(cat, lang.code)).length === categories.length ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tableau des traductions */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Traductions par Catégorie
          </Typography>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Catégorie</TableCell>
                  <TableCell>Slug</TableCell>
                  {LANGUAGES.map((lang) => (
                    <TableCell key={lang.code} align="center">
                      {lang.flag} {lang.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((category) => (
                  <TableRow key={category.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {category.name}
                      </Typography>
                      {category.description && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          {category.description.substring(0, 50)}...
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={category.slug} size="small" variant="outlined" />
                    </TableCell>
                    {LANGUAGES.map((lang) => (
                      <TableCell key={lang.code} align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <Typography variant="body2">
                            {getTranslation(category, lang.code)}
                          </Typography>
                          <Tooltip title={`Modifier la traduction ${lang.name}`}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditTranslation(category, lang.code)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog d'édition */}
      <Dialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, category: null, language: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Modifier la traduction
          {editDialog.language && (
            <Chip
              label={LANGUAGES.find(l => l.code === editDialog.language)?.name}
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Traduction"
            value={translationValue}
            onChange={(e) => setTranslationValue(e.target.value)}
            margin="normal"
            variant="outlined"
            placeholder={editDialog.category?.name}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, category: null, language: null })}>
            Annuler
          </Button>
          <Button
            onClick={handleSaveTranslation}
            variant="contained"
            disabled={submitting || !translationValue.trim()}
          >
            {submitting ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryTranslations;
